import { Badge, Checkbox, Label, Modal, Textarea } from '@crm/ui';
import { Lock } from 'lucide-react';
import { useState } from 'react';
import { FormField } from '@/components';
import { toErrorMessage } from '@/modules/auth/utils';
import type { LicensePlan, PlanInput } from '@/modules/billing';
import { useCreatePlan, useUpdatePlan } from '../api';

interface PlanFormModalProps {
  /** Nulo = criar um plano novo. */
  plano: LicensePlan | null;
  onClose: () => void;
}

function linhas(texto: string): string[] {
  return texto
    .split('\n')
    .map((linha) => linha.trim())
    .filter(Boolean);
}

/**
 * WhatsApp Bot e Marketing sao recursos com checkbox proprio: marcar entra
 * direto na lista de vantagens (o cliente ve na landing) sem poder ser
 * apagado por la - so desmarcando o checkbox. Por isso ficam fora do texto
 * livre da textarea, como chips travados.
 */
const RECURSOS_AUTOMATICOS = {
  whatsappBot: 'Assistente de WhatsApp (bot)',
  whatsappMarketing: 'Campanhas de WhatsApp (marketing)',
} as const;

function semRecursosAutomaticos(features: string[]): string[] {
  const automaticos = new Set<string>(Object.values(RECURSOS_AUTOMATICOS));
  return features.filter((feature) => !automaticos.has(feature));
}

/**
 * Cria ou edita um plano - o mesmo Plan que cobra de verdade e que a landing
 * do SaaS mostra. `features`/`businessFeatures` sao uma linha por item: mais
 * facil de editar do que uma lista com botao de adicionar/remover.
 */
export function PlanFormModal({ plano, onClose }: PlanFormModalProps) {
  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();

  const [name, setName] = useState(plano?.name ?? '');
  const [description, setDescription] = useState(plano?.description ?? '');
  const [monthlyPrice, setMonthlyPrice] = useState(String(plano?.monthlyPrice ?? 0));
  const [introPrice, setIntroPrice] = useState(
    plano?.introPrice != null ? String(plano.introPrice) : '',
  );
  const [introMonths, setIntroMonths] = useState(String(plano?.introMonths ?? 0));
  const [includedMessages, setIncludedMessages] = useState(
    plano?.includedMessages != null ? String(plano.includedMessages) : '',
  );
  const [maxUnits, setMaxUnits] = useState(plano?.maxUnits != null ? String(plano.maxUnits) : '');
  const [maxUsers, setMaxUsers] = useState(plano?.maxUsers != null ? String(plano.maxUsers) : '');
  const [unlimitedUsers, setUnlimitedUsers] = useState(plano?.maxUsers == null);
  const [businessMultiplier, setBusinessMultiplier] = useState(
    String(plano?.businessMultiplier ?? 1),
  );
  const [ctaLabel, setCtaLabel] = useState(plano?.ctaLabel ?? '');
  const [order, setOrder] = useState(String(plano?.order ?? 0));
  const [highlighted, setHighlighted] = useState(plano?.highlighted ?? false);
  const [isActive, setIsActive] = useState(plano?.isActive ?? true);
  const [features, setFeatures] = useState(
    semRecursosAutomaticos(plano?.features ?? []).join('\n'),
  );
  const [businessFeatures, setBusinessFeatures] = useState(
    plano?.businessFeatures.join('\n') ?? '',
  );
  const [whatsappBot, setWhatsappBot] = useState(plano?.whatsappBot ?? false);
  const [whatsappMarketing, setWhatsappMarketing] = useState(plano?.whatsappMarketing ?? false);
  const [erro, setErro] = useState<string | null>(null);

  const salvando = createPlan.isPending || updatePlan.isPending;

  async function salvar() {
    setErro(null);
    const input: PlanInput = {
      name: name.trim(),
      description: description.trim() || null,
      monthlyPrice: Number(monthlyPrice) || 0,
      introPrice: introPrice.trim() ? Number(introPrice) : null,
      introMonths: Number(introMonths) || 0,
      includedMessages: includedMessages.trim() ? Number(includedMessages) : null,
      maxUnits: maxUnits.trim() ? Number(maxUnits) : null,
      maxUsers: unlimitedUsers ? null : maxUsers.trim() ? Number(maxUsers) : null,
      businessMultiplier: Number(businessMultiplier) || 1,
      ctaLabel: ctaLabel.trim() || null,
      order: Number(order) || 0,
      highlighted,
      isActive,
      whatsappBot,
      whatsappMarketing,
      // Os recursos automaticos entram primeiro, na frente do que foi
      // digitado a mao - e o checkbox, nao a textarea, que decide se ficam.
      features: [
        ...(whatsappBot ? [RECURSOS_AUTOMATICOS.whatsappBot] : []),
        ...(whatsappMarketing ? [RECURSOS_AUTOMATICOS.whatsappMarketing] : []),
        ...linhas(features),
      ],
      businessFeatures: linhas(businessFeatures),
    };

    try {
      if (plano) await updatePlan.mutateAsync({ id: plano.id, input });
      else await createPlan.mutateAsync(input);
      onClose();
    } catch (error) {
      setErro(toErrorMessage(error));
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={plano ? `Editar ${plano.name}` : 'Novo plano'}
      subtitle="Mesmos dados que cobram de verdade e que a landing do SaaS mostra."
      size="large"
      actions={[
        { label: 'Cancelar', variant: 'outline', onClick: onClose },
        { label: 'Salvar', onClick: salvar, loading: salvando, type: 'submit' },
      ]}
    >
      <div className="space-y-4">
        {erro ? <p className="text-sm text-destructive">{erro}</p> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Nome" value={name} onChange={(event) => setName(event.target.value)} />
          <FormField
            label="Subtítulo (opcional)"
            placeholder="Ex.: Plano Free"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <FormField
            label="Mensalidade normal (R$)"
            type="number"
            min={0}
            step="0.01"
            value={monthlyPrice}
            onChange={(event) => setMonthlyPrice(event.target.value)}
          />
          <FormField
            label="Mensalidade promocional (R$)"
            type="number"
            min={0}
            step="0.01"
            placeholder="Sem promocao"
            value={introPrice}
            onChange={(event) => setIntroPrice(event.target.value)}
          />
          <FormField
            label="Meses promocionais"
            type="number"
            min={0}
            value={introMonths}
            onChange={(event) => setIntroMonths(event.target.value)}
          />
          <FormField
            label="Créditos incluídos"
            type="number"
            min={0}
            placeholder="Ilimitado"
            value={includedMessages}
            onChange={(event) => setIncludedMessages(event.target.value)}
          />
          <FormField
            label="Multiplicador Empresarial"
            type="number"
            min={0}
            step="0.1"
            value={businessMultiplier}
            onChange={(event) => setBusinessMultiplier(event.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            label="Máx. unidades"
            type="number"
            min={0}
            placeholder="Ilimitado"
            value={maxUnits}
            onChange={(event) => setMaxUnits(event.target.value)}
          />
          <div className="flex items-end gap-3">
            <FormField
              className="min-w-0 flex-1"
              label="Até usuários"
              type="number"
              min={1}
              placeholder="Quantidade"
              value={maxUsers}
              disabled={unlimitedUsers}
              onChange={(event) => setMaxUsers(event.target.value)}
            />
            <Checkbox
              className="mb-2 shrink-0"
              label="Ilimitado"
              checked={unlimitedUsers}
              onChange={(event) => setUnlimitedUsers(event.target.checked)}
            />
          </div>
          <FormField
            label="Ordem na landing"
            type="number"
            min={0}
            value={order}
            onChange={(event) => setOrder(event.target.value)}
          />
        </div>

        <FormField
          label="Texto do botão na landing"
          placeholder="Ex.: Assinar Pro"
          value={ctaLabel}
          onChange={(event) => setCtaLabel(event.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Recursos (um por linha)</Label>
            <div className="space-y-2 rounded-md border border-input p-3">
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <Checkbox
                  label="Inclui WhatsApp Bot"
                  checked={whatsappBot}
                  onChange={(event) => setWhatsappBot(event.target.checked)}
                />
                <Checkbox
                  label="Inclui WhatsApp Marketing"
                  checked={whatsappMarketing}
                  onChange={(event) => setWhatsappMarketing(event.target.checked)}
                />
              </div>
              {whatsappBot || whatsappMarketing ? (
                <div className="flex flex-wrap gap-2">
                  {whatsappBot ? (
                    <Badge variant="secondary" className="gap-1">
                      <Lock className="h-3 w-3" />
                      {RECURSOS_AUTOMATICOS.whatsappBot}
                    </Badge>
                  ) : null}
                  {whatsappMarketing ? (
                    <Badge variant="secondary" className="gap-1">
                      <Lock className="h-3 w-3" />
                      {RECURSOS_AUTOMATICOS.whatsappMarketing}
                    </Badge>
                  ) : null}
                  <p className="w-full text-xs text-muted-foreground">
                    Entram automaticamente na lista de vantagens do plano. So saem desmarcando o
                    checkbox acima.
                  </p>
                </div>
              ) : null}
              <Textarea
                rows={4}
                placeholder="Outros recursos, um por linha"
                value={features}
                onChange={(event) => setFeatures(event.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Recursos só no Empresarial (um por linha)</Label>
            <Textarea
              rows={5}
              value={businessFeatures}
              onChange={(event) => setBusinessFeatures(event.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-6">
          <Checkbox
            label="Destacar na landing"
            checked={highlighted}
            onChange={(event) => setHighlighted(event.target.checked)}
          />
          <Checkbox
            label="Ativo (aparece na landing e pode ser contratado)"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
        </div>
      </div>
    </Modal>
  );
}
